import { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { getChartTheme, detectDarkMode, observeDarkMode } from '../chartTheme';
import { statusToColor } from './formatValue';

const MARKER_STYLES = {
    buy: { symbol: 'triangle', color: '#22C55E', label: '▲' },
    sell: { symbol: 'triangle-down', color: '#EF4444', label: '▼' },
    event: { symbol: 'circle', color: '#6B7280', label: '●' },
};

const DEFAULT_SERIES_COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function LineChart({ title, stat, series, markers }) {
    const chartRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);

    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    useEffect(() => {
        if (!chartRef.current || !series || series.length === 0) return;

        const theme = getChartTheme(isDarkMode);

        const chartSeries = series.map((s, i) => ({
            name: s.name,
            data: (s.data || []).map(d => {
                if (Array.isArray(d)) {
                    const ts = d[0] < 1e12 ? d[0] * 1000 : d[0];
                    return [ts, d[1]];
                }
                return d;
            }),
            color: s.color || DEFAULT_SERIES_COLORS[i % DEFAULT_SERIES_COLORS.length],
            lineWidth: 2,
            marker: { enabled: false },
        }));

        if (markers && markers.length > 0) {
            const markerGroups = {};
            markers.forEach(m => {
                const type = m.type || 'event';
                if (!markerGroups[type]) markerGroups[type] = [];
                const ts = m.x < 1e12 ? m.x * 1000 : m.x;
                const style = MARKER_STYLES[type] || MARKER_STYLES.event;

                const closestSeries = chartSeries[0];
                let yValue = null;
                if (closestSeries && closestSeries.data.length > 0) {
                    let minDiff = Infinity;
                    closestSeries.data.forEach(point => {
                        const diff = Math.abs(point[0] - ts);
                        if (diff < minDiff) {
                            minDiff = diff;
                            yValue = point[1];
                        }
                    });
                }

                markerGroups[type].push({
                    x: ts,
                    y: yValue,
                    marker: {
                        symbol: style.symbol,
                        fillColor: style.color,
                        lineColor: style.color,
                        lineWidth: 1,
                        radius: 6,
                        enabled: true,
                    },
                });
            });

            Object.entries(markerGroups).forEach(([type, points]) => {
                const style = MARKER_STYLES[type] || MARKER_STYLES.event;
                chartSeries.push({
                    name: markers.find(m => m.type === type)?.label || type,
                    type: 'scatter',
                    data: points,
                    color: style.color,
                    marker: {
                        symbol: style.symbol,
                        radius: 6,
                    },
                    zIndex: 10,
                });
            });
        }

        Highcharts.chart(chartRef.current, {
            chart: {
                ...theme.chart,
                type: 'line',
                height: 300,
            },
            title: { text: null },
            credits: theme.credits,
            legend: {
                enabled: chartSeries.length > 1,
                align: 'center',
                verticalAlign: 'bottom',
                itemStyle: {
                    color: isDarkMode ? '#a0a0a0' : '#5f5e5a',
                    fontSize: '11px',
                    fontWeight: '400',
                },
            },
            xAxis: {
                ...theme.xAxis,
                type: 'datetime',
            },
            yAxis: {
                ...theme.yAxis,
                title: { text: null },
            },
            tooltip: {
                ...theme.tooltip,
                xDateFormat: '%d/%m/%Y',
                shared: true,
            },
            plotOptions: {
                line: {
                    marker: { enabled: false },
                    states: { hover: { lineWidth: 3 } },
                },
            },
            series: chartSeries,
        });
    }, [series, markers, isDarkMode]);

    if (!series || series.length === 0) return null;

    return (
        <div>
            {title && <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</div>}

            {stat && stat.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                    {stat.map((s, i) => (
                        <div key={i} className="bg-gray-100 dark:bg-white/10 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">{s.label}</div>
                            <div className={`text-lg font-medium ${
                                s.status ? statusToColor(s.status)
                                : typeof s.value === 'string' && s.value.startsWith('+') ? 'text-green-500'
                                : typeof s.value === 'string' && s.value.startsWith('-') ? 'text-red-500'
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                                {s.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div ref={chartRef} className="w-full" />
        </div>
    );
}
